import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, ArrowLeft } from "lucide-react";
import axios from "axios";
import { fallbackServices } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://api.elysana-massage.de";
const API = `${BACKEND_URL}/api`;

const AllServicesPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState(fallbackServices);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/services`, {
        timeout: 5000,
      });
      
      // Ensure response.data is an array
      if (Array.isArray(response.data) && response.data.length > 0) {
        setServices(response.data);
      } else {
        // Fallback to static data if API returns invalid data
        setServices(fallbackServices);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      // Fallback to static data on error
      setServices(fallbackServices);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookNow = (serviceId) => {
    navigate(`/book/${serviceId}`);
  };

  const serviceImages = {
    "aroma-oil": "https://images.pexels.com/photos/7148532/pexels-photo-7148532.jpeg",
    "hot-stone": "https://images.unsplash.com/photo-1610402601271-5b4bd5b3eba4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxob3QlMjBzdG9uZSUyMG1hc3NhZ2UlMjB0aGVyYXB5fGVufDB8fHx8MTc2NDY4Mzk5N3ww&ixlib=rb-4.1.0&q=85",
    "abhyanga": "https://images.unsplash.com/photo-1649751295468-953038600bef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxheXVydmVkaWMlMjBtYXNzYWdlJTIwb2lsJTIwdHJlYXRtZW50fGVufDB8fHx8MTc2NDY4NDAxMnww&ixlib=rb-4.1.0&q=85",
    "full-body": "https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxzcGElMjBtYXNzYWdlJTIwdGhlcmFweXxlbnwwfHx8fDE3NjQ2ODM5OTB8MA&ixlib=rb-4.1.0&q=85",
    "thai": "https://images.unsplash.com/photo-1559185590-d545a0c5a1dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxmb290JTIwbWFzc2FnZSUyMHNwYSUyMHRoZXJhcHl8ZW58MHx8fHwxNzY0Njg0MDA0fDA&ixlib=rb-4.1.0&q=85",
    "sanjeevani": "https://images.pexels.com/photos/6724401/pexels-photo-6724401.jpeg",
    "leg-foot": "https://images.pexels.com/photos/5240642/pexels-photo-5240642.jpeg",
    "mukabhyanga": "https://images.unsplash.com/photo-1722350766824-f8520e9676ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxmYWNpYWwlMjBtYXNzYWdlJTIwc3BhJTIwcmVsYXhpbmd8ZW58MHx8fHwxNzY0Njg0NzI3fDA&ixlib=rb-4.1.0&q=85",
    "back": "https://images.pexels.com/photos/6560266/pexels-photo-6560266.jpeg"
  };

  return (
    <div className="min-h-screen bg-background grain-texture">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-4 z-50 mx-4 md:mx-8 mt-4"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 rounded-full border border-white/20 bg-white/70 backdrop-blur-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/up1s5d57_Screenshot_2025-12-10_at_01.58.41-removebg-preview.png"
                alt="Elysana Logo"
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-primary hover:text-accent transition-colors">Startseite</a>
              <a href="/all-services" className="text-accent font-medium">Alle Leistungen</a>
              <a href="/faq" className="text-primary hover:text-accent transition-colors">FAQ</a>
              <a href="/contact" className="text-primary hover:text-accent transition-colors">Kontakt</a>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Header Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-primary hover:text-accent transition-colors mb-8"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft size={20} />
            <span>Zurück zur Startseite</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-primary mb-6">Alle Leistungen</h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Entdecken Sie unser komplettes Angebot an professionellen Massage- und Wellnessbehandlungen
            </p>
          </motion.div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-text-secondary">Leistungen werden geladen...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="service-card group flex flex-col"
                  onClick={() => handleBookNow(service.id)}
                  data-testid={`service-directory-card-${service.id}`}
                >
                  <div className="h-64 rounded-xl overflow-hidden mb-6">
                    <img 
                      src={serviceImages[service.id]}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-3">{service.title}</h3>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {service.originalPrice && (
                        <p className="text-lg text-text-muted line-through">{service.originalPrice}</p>
                      )}
                      <p className="text-2xl font-bold text-accent">{service.price}</p>
                    </div>
                    <p className="text-sm text-text-muted">{service.duration}</p>
                  </div>
                  <p className="text-text-secondary mb-6 flex-grow">{service.description}</p>
                  <button 
                    className="btn-primary w-full mt-auto"
                    data-testid={`book-service-btn-${service.id}`}
                  >
                    Jetzt buchen
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AllServicesPage;